import React, { useState, useEffect } from 'react';
import { ZoneInfo } from '../types';
import { MAJOR_TIMEZONES } from '../utils/timeUtils';
import { Globe, Download, Search } from 'lucide-react';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const TimeZones: React.FC = () => {
  const [baseTime, setBaseTime] = useState<string>(new Date().toISOString().slice(0, 16));
  const [filter, setFilter] = useState('');
  const [zones, setZones] = useState<ZoneInfo[]>([]);

  useEffect(() => {
    // Generate zone data
    const date = new Date(baseTime);
    const generated: ZoneInfo[] = MAJOR_TIMEZONES.map(tz => {
      try {
        const zonedDate = toZonedTime(date, tz);
        const offsetMin = -zonedDate.getTimezoneOffset(); // Note: This might be inaccurate in pure JS without full ICU, relying on Intl here
        
        // Better way with Intl
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'shortOffset',
            hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
        const parts = formatter.formatToParts(date);
        const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '';
        
        // Parse "GMT-5" or "GMT+05:30" manually or just store string
        return {
          name: tz,
          time: formatter.format(date),
          offsetStr: offsetPart,
          offset: 0, // Simplified for now
          dst: false // Hard to detect purely without library heft, skipping exact boolean
        };
      } catch (e) {
        return null;
      }
    }).filter(z => z !== null) as ZoneInfo[];
    
    setZones(generated);
  }, [baseTime]);

  const exportCSV = () => {
    const headers = ['Region', 'Time', 'Offset'];
    const rows = zones.map(z => [z.name, z.time, z.offsetStr]);
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "timezones.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredZones = zones.filter(z => z.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Globe size={24} className="text-sky-500" /> World Time Zones
        </h2>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <label className="block text-sm font-medium text-slate-600 mb-1">Base Date & Time</label>
            <input 
              type="datetime-local" 
              value={baseTime}
              onChange={(e) => setBaseTime(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none"
            />
          </div>
          <div className="flex-1 w-full relative">
            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Filter by city/region..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none"
            />
          </div>
          <button 
            onClick={exportCSV}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 flex items-center gap-2 whitespace-nowrap"
          >
            <Download size={18} /> Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Region / City</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Local Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Offset</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredZones.map((z) => (
                <tr key={z.name} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{z.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-mono">{z.time}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-sky-600 font-mono">{z.offsetStr}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredZones.length === 0 && (
            <div className="p-8 text-center text-slate-500">No time zones found matching your filter.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeZones;